import { AppFunctionComponent } from "../../../types"
import { BLOCKS } from "@contentful/rich-text-types"
import { graphql, useStaticQuery } from "gatsby"
import React, { FormEvent, ReactNode, useState } from "react"
import styled from "styled-components"
import LikeIcon from "../../../images/like.inline.svg"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"
import { ContentfulSection, QueryResultFlat } from "../../../types"
import { BasicFullButton } from "../../../ui/components/button/button.component"
import { RawLink } from "../../../ui/components/link/link.component"
import { Annotation } from "../../common/general-styles.component"
import { PrimaryHeading } from "../../common/heading.component"
import { Image } from "../../common/image.component"
import { Modal, ModalType } from "../../../ui/index"
import { TextInput } from "../../common/text-input.component"
import Thanks from "../../common/thanks.component"
import useSendMissingPhoneRequest from "./use-send-missing-phone-request.hook"
import useTranslationHook from "../../../hooks/use-translation.hook"
import { useLocalizationContext } from "../../../context/localization.context"

const Subtitle = styled.p`
  max-width: 550px;
  margin-bottom: ${30 / 14}em;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin-bottom: ${50 / 16}em;
  }
`

const Description = styled(Annotation)`
  max-width: 460px;
  margin-bottom: ${30 / 11}em;
  letter-spacing: -0.3px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin-bottom: ${50 / 14}em;
    line-height: ${20 / 14}em;
  }
`

const Input = styled(TextInput)`
  width: 100%;
  padding-left: 0;
  margin-left: 0;
  margin-bottom: ${15 / 14}em;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    max-width: 470px;

    &:first-of-type {
      margin-bottom: ${25 / 16}em;
    }
  }
`

const ModalButton = styled(BasicFullButton)`
  max-width: none;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    max-width: 240px;
  }
`

const TellUsModalBackgroundImage = styled(Image)`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    display: block;
    z-index: -1;
    position: absolute;
    right: 0;
    width: 80%;
    bottom: 70px;
  }
`

const Form = styled.form`
  max-width: 610px;

  > ${PrimaryHeading} {
    margin: 0 auto ${15 / 37}em;
  }
`

interface Props {
  active: boolean
  onClose: () => void
}

export interface SarCheckerTellUsModalQueryResult {
  content: QueryResultFlat<ContentfulSection>
  thanksContent: QueryResultFlat<ContentfulSection>
}

const TellUsModal: AppFunctionComponent<Props> = ({ active, onClose }) => {
  const {
    content: { nodes },
    thanksContent: { nodes: thanksNodes },
  }: SarCheckerTellUsModalQueryResult = useStaticQuery(query)
  const { locale } = useLocalizationContext()
  const translatedContent =
    nodes.find((node) => node.node_locale === locale) || nodes[0]
  const translatedThanksContent =
    thanksNodes.find((node) => node.node_locale === locale) || thanksNodes[0]

  const { text, title, image, desktopImage } = translatedContent
  const { title: thanksText } = translatedThanksContent

  const [phoneModelValue, setPhoneModelValue] = useState("")
  const [phoneModelError, setPhoneModelError] = useState("")

  const [emailValue, setEmailValue] = useState("")

  const { pending, sendRequest } = useSendMissingPhoneRequest()
  const [requestSent, setRequestSent] = useState(false)
  const { translate } = useTranslationHook()

  const submit = (event: FormEvent) => {
    event.preventDefault()

    if (phoneModelValue) {
      sendRequest(phoneModelValue, emailValue).finally(() =>
        setRequestSent(true)
      )
    } else {
      setPhoneModelError("Missing phone model")
    }
  }

  const setInitState = () => {
    onClose()
    setPhoneModelValue("")
    setPhoneModelError("")
    setEmailValue("")
    setRequestSent(false)
  }

  return (
    <Modal modalType={ModalType.Box} active={active} onClose={setInitState}>
      {!requestSent ? (
        <Form onSubmit={submit}>
          <PrimaryHeading renderAs={"h1"}>{title}</PrimaryHeading>
          {text && (
            <RichText
              content={text}
              options={{
                [BLOCKS.PARAGRAPH]: (_: any, children: ReactNode) => (
                  <Subtitle>{children}</Subtitle>
                ),
              }}
            />
          )}
          <Input
            onValueChange={setPhoneModelValue}
            value={phoneModelValue}
            type={"text"}
            label={translate("sar-enter-phone")}
            error={phoneModelError}
          />
          <Input
            optional
            onValueChange={setEmailValue}
            value={emailValue}
            type={"email"}
            label={translate("newsletter-your-email")}
          />
          <Description>
            {image!.description}{" "}
            <RawLink to="/legal/privacy-policy/">
              {translate("Privacy Policy")}
            </RawLink>
          </Description>
          <ModalButton
            pending={pending}
            disabled={Boolean(!phoneModelValue && phoneModelError)}
          >
            {translate("Send")}
          </ModalButton>
          <TellUsModalBackgroundImage image={desktopImage!.gatsbyImageData} />
        </Form>
      ) : (
        <Thanks onClose={setInitState} text={thanksText!} buttonText={"back"}>
          <LikeIcon />
        </Thanks>
      )}
    </Modal>
  )
}

export default TellUsModal

const query = graphql`
  query SarCheckerTellUsModalQuery {
    content: allContentfulSection(
      filter: { contentfulid: { eq: "sar-checker/tell-us-modal" } }
    ) {
      nodes {
        title
        node_locale
        text {
          raw
        }
        image {
          description
        }
        desktopImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 1000
          )
        }
      }
    }
    thanksContent: allContentfulSection(
      filter: { contentfulid: { eq: "sar-checker/tell-us-modal-thanks" } }
    ) {
      nodes {
        title
        node_locale
      }
    }
  }
`
